import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { GooglePlaceResult } from '@app/shared/directives/google-places.directive';
import { NotificationType } from '@app/shared/interfaces';
import { BillingService } from '@app/shared/services/billing/billing.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss'],
})
export class BillingFormComponent implements OnInit {
  public loading$ = new BehaviorSubject<boolean>(true);
  public billingForm = this.fb.group({
    business: [true],
    companyName: [''],
    vat: [''],
    taxId: [''],
    street1: ['', [Validators.required]],
    street2: [''],
    city: ['', [Validators.required]],
    region: ['', [Validators.required]],
    postcode: ['', [Validators.required]],
    country: ['', [this.validateCountry.bind(this)]],
  });
  public billingDetails$ = this.billingService.getBilling().pipe(
    tap(() => this.loading$.next(false)),
    tap((billingData) => {
      this.billingForm.patchValue({
        business: billingData.business,
        companyName: billingData.company,
        taxId: billingData.tax_id,
        vat: billingData.vat,
        street1: billingData.street_line1,
        street2: billingData.street_line2,
        city: billingData.city,
        region: billingData.region,
        postcode: billingData.postal_code,
        country: billingData.country,
      });
    })
  );

  saving: boolean;
  saveStatus: number;

  countries: any[];
  filteredCountries: Observable<any[]>;

  @Input() embedded = false;
  @Output() saved: EventEmitter<boolean> = new EventEmitter();

  public showVatField$ = new BehaviorSubject<boolean>(false);
  public showTaxIdField$ = new BehaviorSubject<boolean>(true);

  constructor(
    private billingService: BillingService,
    private fb: UntypedFormBuilder,
    private toast: ToastNotificationsService
  ) {}

  ngOnInit() {
    this.billingService.getBillingCountries().subscribe((res) => {
      this.countries = res;

      this.filteredCountries = this.billingForm.get('country').valueChanges.pipe(
        startWith(''),
        map((value) => (typeof value === 'string' ? value : value.name)),
        map((name) => (name ? this._filterCountry(name) : this.countries.slice())),
        tap((countries) => {
          const selectedCountry = this.countries.find((c) => c.name === this.billingForm.get('country').value);
          const showVat = selectedCountry?.is_eu;
          this.showVatField$.next(showVat);
          this.showTaxIdField$.next(!showVat || selectedCountry?.alpha2code === 'AU');
        })
      );
    });

    this.setupConditionalFormValidators();
  }

  private _filterCountry(name: string): any[] {
    const filterValue = name.toLowerCase();

    return this.countries.filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  setupConditionalFormValidators() {
    this.billingForm.get('business').valueChanges.subscribe((business: boolean) => {
      if (business) {
        this.billingForm.get('companyName').setValidators([Validators.required]);
      } else {
        this.billingForm.get('companyName').clearValidators();
      }
      this.billingForm.get('companyName').updateValueAndValidity();
    });
  }

  save() {
    this.saving = true;
    this.saveStatus = 1;

    const payload = {
      business: this.billingForm.get('business').value,
      company: this.billingForm.get('companyName').value,
      tax_id: this.billingForm.get('taxId').value,
      vat: this.billingForm.get('vat').value,
      country: this.billingForm.get('country').value,
      street_line1: this.billingForm.get('street1').value,
      street_line2: this.billingForm.get('street2').value,
      city: this.billingForm.get('city').value,
      region: this.billingForm.get('region').value,
      postal_code: this.billingForm.get('postcode').value,
    };

    this.billingService.saveBilling(payload).subscribe(
      (res) => {
        this.saving = false;

        if (res.error) {
          this.saveStatus = null;
          this.toast.open('There was an error saving your billing information', 'Dismiss', NotificationType.Error);
        } else {
          this.saved.emit(true);
          this.saveStatus = 2;
          this.toast.open('Billing information saved successfully', 'Dismiss', NotificationType.Success);
          setTimeout(() => {
            this.saveStatus = null;
          }, 2000);
        }
      },
      (err) => {
        this.saving = false;
        this.toast.open('There was an error saving your billing information', 'Dismiss', NotificationType.Error);
      },
      () => {
        this.saving = false;
      }
    );
  }

  validateCountry(control: UntypedFormControl): ValidationErrors | null {
    if (this.countries) {
      const selectedCountry = this.countries.find((country) => country.name === control.value);
      if (!selectedCountry) {
        return { invalidCountry: true };
      }
    }
    return null;
  }

  onPlaceChanged(place: GooglePlaceResult) {
    this.billingForm.patchValue({
      street1: place.name,
      city: place.city,
      region: place.state,
      postcode: place.postalCode,
      country: place.country,
    });
  }
}
